import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, TrendingUp, Target, Calendar, ChevronDown, ChevronUp, Home, Download } from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';

const StatBadge = ({ label, value, color = 'stone' }) => (
  <div className={cn('text-center p-3 rounded-lg', `bg-${color}-900/30 border border-${color}-700/50`)}>
    <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
    <p className={cn('text-[10px] uppercase tracking-wider', `text-${color}-400`)}>{label}</p>
  </div>
);

export default function SeasonStatsPage() {
  const navigate = useNavigate();
  const { currentTeamId } = useGameStore();
  const { token } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState('all');
  const [seasons, setSeasons] = useState([]);
  const [sortCol, setSortCol] = useState('avg');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!currentTeamId) { navigate('/'); return; }
    loadSeasons();
    loadStats();
  }, [currentTeamId]);

  useEffect(() => {
    if (currentTeamId) loadStats();
  }, [season]);

  const loadSeasons = async () => {
    try {
      const res = await api.get(`/seasons?team_id=${currentTeamId}`);
      setSeasons(res.data || []);
    } catch {}
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const params = season !== 'all' ? `?season=${encodeURIComponent(season)}` : '';
      const res = await api.get(`/stats/season/${currentTeamId}${params}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally { setLoading(false); }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedBatting = stats?.batting?.slice().sort((a, b) => {
    const av = a[sortCol] ?? 0, bv = b[sortCol] ?? 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  }) || [];

  const totals = sortedBatting.reduce((t, p) => ({
    ab: t.ab + p.ab, h: t.h + p.h, singles: t.singles + p.singles, doubles: t.doubles + p.doubles,
    triples: t.triples + p.triples, hr: t.hr + p.hr, rbi: t.rbi + p.rbi, bb: t.bb + p.bb,
    k: t.k + p.k, hbp: t.hbp + p.hbp
  }), { ab: 0, h: 0, singles: 0, doubles: 0, triples: 0, hr: 0, rbi: 0, bb: 0, k: 0, hbp: 0 });
  const teamAvg = totals.ab > 0 ? (totals.h / totals.ab).toFixed(3) : '.000';

  const SortHeader = ({ col, label, className = '' }) => (
    <th className={cn('py-2 px-1.5 cursor-pointer hover:text-green-400 transition-colors select-none', className)}
      onClick={() => handleSort(col)}>
      <div className="flex items-center justify-center gap-0.5">
        <span>{label}</span>
        {sortCol === col && (sortDir === 'desc' ? <ChevronDown size={10} /> : <ChevronUp size={10} />)}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 p-4 md:p-6" data-testid="season-stats-page">
      <div className="max-w-6xl mx-auto space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft size={16} />
            </Button>
            <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="home-btn">
              <Home size={16} />
            </Button>
            <div>
              <h1 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                SEASON STATS
              </h1>
              <p className="text-stone-500 text-xs">{stats?.team_name}</p>
            </div>
          </div>
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger className="w-44 bg-stone-800 border-stone-600 text-white" data-testid="season-select">
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {seasons.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              {/* Also show seasons from games that might not have season records */}
              {stats?.season && stats.season !== 'All Seasons' && !seasons.find(s => s.name === stats.season) && (
                <SelectItem value={stats.season}>{stats.season}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Export Button */}
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="text-stone-300 border-stone-600 hover:bg-stone-700"
            data-testid="export-season-csv-btn"
            onClick={() => {
              const params = season !== 'all' ? `?season=${encodeURIComponent(season)}` : '';
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/season-stats/${currentTeamId}${params}`;
              const a = document.createElement('a'); a.href = url; a.download = 'season_stats.csv'; a.click();
            }}>
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>

        {/* Team Record Overview */}
        <Card className="border-0 bg-gradient-to-r from-green-900/50 to-stone-800 shadow-xl">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="team-overview">
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stats?.record?.wins || 0}-{stats?.record?.losses || 0}{stats?.record?.ties ? `-${stats.record.ties}` : ''}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-green-400">Record</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stats?.record?.win_pct ? (stats.record.win_pct * 100).toFixed(0) + '%' : '-'}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Win %</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{stats?.runs_scored || 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Runs For</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{stats?.runs_against || 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Runs Against</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className={cn('text-3xl font-extrabold', (stats?.run_differential || 0) >= 0 ? 'text-green-400' : 'text-red-400')} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {(stats?.run_differential || 0) > 0 ? '+' : ''}{stats?.run_differential || 0}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Run Diff</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{stats?.games_played || 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Games</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-stone-800/80">
                <p className="text-3xl font-extrabold text-green-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{teamAvg}</p>
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Team AVG</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batting Stats */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <TrendingUp size={16} className="text-green-400" /> BATTING LEADERBOARD
            </CardTitle>
            <p className="text-stone-500 text-[10px]">Click column headers to sort</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="season-batting-table">
              <thead>
                <tr className="border-b border-stone-700 text-stone-400">
                  <th className="text-left py-2 px-1.5">#</th>
                  <th className="text-left py-2 px-1.5 min-w-[90px]">Player</th>
                  <SortHeader col="games" label="G" />
                  <SortHeader col="ab" label="AB" />
                  <SortHeader col="h" label="H" />
                  <SortHeader col="singles" label="1B" />
                  <SortHeader col="doubles" label="2B" />
                  <SortHeader col="triples" label="3B" />
                  <SortHeader col="hr" label="HR" />
                  <SortHeader col="rbi" label="RBI" />
                  <SortHeader col="bb" label="BB" />
                  <SortHeader col="k" label="K" />
                  <SortHeader col="avg" label="AVG" />
                  <SortHeader col="obp" label="OBP" />
                  <SortHeader col="slg" label="SLG" />
                  <SortHeader col="ops" label="OPS" />
                </tr>
              </thead>
              <tbody>
                {sortedBatting.filter(p => p.ab > 0 || p.bb > 0 || p.hbp > 0).map((p, idx) => (
                  <tr key={p.player_id} className={cn('border-b border-stone-700/30 hover:bg-stone-700/30 transition-colors', p.hr > 0 && 'bg-amber-900/10')}>
                    <td className="text-stone-500 py-1.5 px-1.5">{p.jersey}</td>
                    <td className="text-stone-200 py-1.5 px-1.5 font-medium truncate max-w-[90px] cursor-pointer hover:text-green-400 transition-colors" data-testid={`batting-player-link-${p.player_id}`} onClick={() => navigate(`/player-profile/${currentTeamId}/${p.player_id}`)}>{p.name}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.games}</td>
                    <td className="text-center text-stone-300 py-1.5 px-1.5">{p.ab}</td>
                    <td className={cn('text-center py-1.5 px-1.5 font-bold', p.h > 0 ? 'text-green-400' : 'text-stone-500')}>{p.h}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.singles || '-'}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.doubles || '-'}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.triples || '-'}</td>
                    <td className={cn('text-center py-1.5 px-1.5', p.hr > 0 ? 'text-amber-400 font-bold' : 'text-stone-500')}>{p.hr || '-'}</td>
                    <td className={cn('text-center py-1.5 px-1.5', p.rbi > 0 ? 'text-green-300 font-bold' : 'text-stone-500')}>{p.rbi || '-'}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.bb || '-'}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5">{p.k || '-'}</td>
                    <td className={cn('text-center py-1.5 px-1.5 font-mono font-bold', p.avg >= 0.300 ? 'text-green-400' : 'text-stone-300')}>
                      {p.avg.toFixed(3)}
                    </td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5 font-mono">{p.obp.toFixed(3)}</td>
                    <td className="text-center text-stone-400 py-1.5 px-1.5 font-mono">{p.slg.toFixed(3)}</td>
                    <td className={cn('text-center py-1.5 px-1.5 font-mono', p.ops >= 0.800 ? 'text-amber-400 font-bold' : 'text-stone-300')}>
                      {p.ops.toFixed(3)}
                    </td>
                  </tr>
                ))}
                {/* Totals */}
                <tr className="border-t-2 border-stone-600">
                  <td className="py-2 px-1.5" colSpan={2}><span className="text-stone-300 font-bold text-xs">TEAM</span></td>
                  <td className="text-center text-stone-300 py-2 px-1.5">{stats?.games_played || 0}</td>
                  <td className="text-center text-stone-200 py-2 px-1.5 font-bold">{totals.ab}</td>
                  <td className="text-center text-green-400 py-2 px-1.5 font-bold">{totals.h}</td>
                  <td className="text-center text-stone-400 py-2 px-1.5">{totals.singles}</td>
                  <td className="text-center text-stone-400 py-2 px-1.5">{totals.doubles}</td>
                  <td className="text-center text-stone-400 py-2 px-1.5">{totals.triples}</td>
                  <td className="text-center text-amber-400 py-2 px-1.5 font-bold">{totals.hr}</td>
                  <td className="text-center text-green-300 py-2 px-1.5 font-bold">{totals.rbi}</td>
                  <td className="text-center text-stone-400 py-2 px-1.5">{totals.bb}</td>
                  <td className="text-center text-stone-400 py-2 px-1.5">{totals.k}</td>
                  <td className="text-center text-green-400 py-2 px-1.5 font-mono font-bold">{teamAvg}</td>
                  <td colSpan={3}></td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pitching Stats */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Target size={16} className="text-amber-400" /> PITCHING LEADERBOARD
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!stats?.pitching || stats.pitching.length === 0) ? (
              <p className="text-stone-500 text-center py-6 text-sm">No pitching data recorded yet</p>
            ) : (
              <table className="w-full text-sm" data-testid="season-pitching-table">
                <thead>
                  <tr className="border-b border-stone-700 text-stone-400 text-xs">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Pitcher</th>
                    <th className="text-center py-2 px-2">G</th>
                    <th className="text-center py-2 px-2">IP</th>
                    <th className="text-center py-2 px-2">Pitches</th>
                    <th className="text-center py-2 px-2">Pitch/G</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.pitching.map((p) => (
                    <tr key={p.player_id} className="border-b border-stone-700/30 hover:bg-stone-700/30">
                      <td className="text-stone-500 py-2 px-2">{p.jersey}</td>
                      <td className="text-stone-200 py-2 px-2 font-medium cursor-pointer hover:text-green-400 transition-colors" data-testid={`pitching-player-link-${p.player_id}`} onClick={() => navigate(`/player-profile/${currentTeamId}/${p.player_id}`)}>{p.name}</td>
                      <td className="text-center text-stone-300 py-2 px-2">{p.games}</td>
                      <td className="text-center text-stone-300 py-2 px-2">{p.innings}</td>
                      <td className="text-center py-2 px-2">
                        <span className="bg-amber-600/30 text-amber-300 px-2.5 py-0.5 rounded font-bold">{p.total_pitches}</span>
                      </td>
                      <td className="text-center text-stone-400 py-2 px-2">
                        {p.games > 0 ? Math.round(p.total_pitches / p.games) : 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Game Results */}
        {stats?.game_results?.length > 0 && (
          <Card className="border-0 bg-stone-800 shadow-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Calendar size={16} className="text-blue-400" /> GAME RESULTS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1" data-testid="game-results">
                {stats.game_results.map((g) => (
                  <div key={g.game_id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-stone-700/30 cursor-pointer transition-all"
                    onClick={() => navigate(`/game-summary/${currentTeamId}/${g.game_id}`)}
                  >
                    <span className={cn(
                      'w-7 h-7 rounded flex items-center justify-center font-extrabold text-sm',
                      g.result === 'W' ? 'bg-green-600 text-white' : g.result === 'L' ? 'bg-red-600 text-white' : 'bg-stone-600 text-white'
                    )} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {g.result}
                    </span>
                    <span className="text-stone-400 text-xs w-20">{g.date}</span>
                    <span className="text-stone-200 text-sm flex-1">vs {g.opponent}</span>
                    <span className="text-white font-bold text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {g.our_score}-{g.their_score}
                    </span>
                    <Trophy size={14} className="text-stone-600" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
